import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Package, Plus, PencilSimple, Trash, MagnifyingGlass,
  ArrowSquareOut, ShieldCheck, Tag, Sparkle, X, WarningCircle
} from "@phosphor-icons/react";

function generateSlug(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function generateNextCode(products) {
  const codes = (products || [])
    .map((p) => Number(p.productCode))
    .filter((n) => !isNaN(n) && n > 0);
  if (codes.length === 0) return 101;
  return Math.max(...codes) + 1;
}

export default function ProductMaster() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'in_use' | 'unused'
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await api.get("/products");
      setProducts(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const matchSearch =
        (p.productName || "").toLowerCase().includes(search.toLowerCase()) ||
        String(p.productCode ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (p.productSlug || "").toLowerCase().includes(search.toLowerCase());

      if (!matchSearch) return false;
      if (filterMode === "in_use") return (p.leads_count || 0) > 0;
      if (filterMode === "unused") return (p.leads_count || 0) === 0;
      return true;
    });
  }, [products, search, filterMode]);

  const totalLeadsLinked = useMemo(() => {
    return products.reduce((acc, p) => acc + (p.leads_count || 0), 0);
  }, [products]);

  const inUseCount = useMemo(() => {
    return products.filter((p) => (p.leads_count || 0) > 0).length;
  }, [products]);

  const handleOpenAdd = () => {
    setEditingProduct(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (prod) => {
    setEditingProduct(prod);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/products/${deleteConfirm.id}`);
      toast.success(`Product "${deleteConfirm.productName}" deleted`);
      setDeleteConfirm(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete product");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="product-master-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#E5E7EB] pb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-1">
            <span>Master</span>
            <span>/</span>
            <span className="text-[#002FA7]">Product Master</span>
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#111827]">
            Product Master
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Configure system products. Products referenced by leads cannot be deleted.
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          data-testid="add-product-btn"
          className="bg-[#002FA7] text-white font-bold px-4 py-2.5 text-sm hover:bg-[#002280] transition-colors flex items-center justify-center gap-2 shadow-sm shrink-0"
        >
          <Plus size={18} weight="bold" />
          <span>Add Product</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 text-[#002FA7] flex items-center justify-center rounded-lg">
            <Package size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Total Products</div>
            <div className="text-2xl font-black text-[#111827]">{products.length}</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 flex items-center justify-center rounded-lg">
            <ShieldCheck size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">In Active Use</div>
            <div className="text-2xl font-black text-[#111827]">
              {inUseCount} <span className="text-xs font-normal text-[#6B7280]">of {products.length}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-50 text-[#6366F1] flex items-center justify-center rounded-lg">
            <Tag size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Leads Referenced</div>
            <div className="text-2xl font-black text-[#111827]">{totalLeadsLinked}</div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white border border-[#E5E7EB] p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Filter:</span>
          <div className="inline-flex border border-[#D1D5DB] text-xs font-bold">
            <button
              onClick={() => setFilterMode("all")}
              className={`px-3 py-1.5 transition-colors ${filterMode === "all" ? "bg-[#002FA7] text-white" : "hover:bg-[#F3F4F6] text-[#374151]"}`}
            >
              All ({products.length})
            </button>
            <button
              onClick={() => setFilterMode("in_use")}
              className={`px-3 py-1.5 border-l border-[#D1D5DB] transition-colors ${filterMode === "in_use" ? "bg-[#002FA7] text-white" : "hover:bg-[#F3F4F6] text-[#374151]"}`}
            >
              In Use ({inUseCount})
            </button>
            <button
              onClick={() => setFilterMode("unused")}
              className={`px-3 py-1.5 border-l border-[#D1D5DB] transition-colors ${filterMode === "unused" ? "bg-[#002FA7] text-white" : "hover:bg-[#F3F4F6] text-[#374151]"}`}
            >
              Safe to Delete ({products.length - inUseCount})
            </button>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="bg-white border border-[#E5E7EB] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-[11px] font-black uppercase tracking-wider text-[#6B7280]">
                <th className="py-3.5 px-4">Product Name</th>
                <th className="py-3.5 px-4">Lead References</th>
                <th className="py-3.5 px-4">Created / Updated</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB] text-sm">
              {loading ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-[#6B7280]">
                    Loading products...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-[#6B7280]">
                    No products found. Click "Add Product" to create one.
                  </td>
                </tr>
              ) : (
                filtered.map((p) => {
                  const hasLeads = (p.leads_count || 0) > 0;
                  return (
                    <tr key={p.id} className="hover:bg-[#F9FAFB] transition-colors">
                      {/* Name */}
                      <td className="py-3.5 px-4 font-bold text-[#111827]">
                        {p.productName}
                      </td>

                      {/* Leads Count */}
                      <td className="py-3.5 px-4">
                        {hasLeads ? (
                          <Link
                            to={`/leads?product_code=${p.productCode}`}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold bg-blue-50 text-[#002FA7] hover:bg-blue-100 transition-colors"
                            title="View leads for this product"
                          >
                            <span>{p.leads_count} lead{p.leads_count > 1 ? "s" : ""}</span>
                            <ArrowSquareOut size={13} weight="bold" />
                          </Link>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-[#4B5563]">
                            0 leads · Safe to delete
                          </span>
                        )}
                      </td>

                      {/* Dates */}
                      <td className="py-3.5 px-4 text-xs text-[#6B7280]">
                        <div>Created: {p.createdAt ? new Date(p.createdAt).toLocaleDateString('en-IN') : "—"}</div>
                        {p.updatedAt && p.updatedAt !== p.createdAt && (
                          <div className="text-[11px] text-[#9CA3AF]">
                            Updated: {new Date(p.updatedAt).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <button
                            onClick={() => handleOpenEdit(p)}
                            className="p-1.5 text-[#374151] hover:text-[#002FA7] hover:bg-blue-50 transition-colors rounded"
                            title="Edit Product"
                          >
                            <PencilSimple size={18} weight="bold" />
                          </button>

                          <button
                            onClick={() => setDeleteConfirm(p)}
                            disabled={hasLeads}
                            className={`p-1.5 rounded transition-colors ${
                              hasLeads
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-[#EF4444] hover:bg-red-50"
                            }`}
                            title={
                              hasLeads
                                ? `Cannot delete: ${p.leads_count} lead(s) are using this product`
                                : "Delete Product"
                            }
                          >
                            <Trash size={18} weight="bold" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {modalOpen && (
        <ProductFormModal
          product={editingProduct}
          products={products}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            fetchProducts();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full p-6 border border-[#111827] shadow-xl space-y-4">
            <div className="flex items-center gap-3 text-[#EF4444]">
              <WarningCircle size={28} weight="fill" />
              <h3 className="font-heading font-black text-xl tracking-tight text-[#111827]">
                Delete Product
              </h3>
            </div>

            <p className="text-sm text-[#4B5563]">
              Are you sure you want to delete product{" "}
              <strong className="text-[#111827]">"{deleteConfirm.productName}"</strong>?
            </p>

            <div className="p-3 bg-gray-50 border border-gray-200 text-xs text-[#6B7280]">
              This action cannot be undone. System verified 0 leads are currently using this product.
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-[#D1D5DB] text-sm font-bold hover:bg-[#F3F4F6] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-[#EF4444] text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Delete Product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductFormModal({ product, products = [], onClose, onSaved }) {
  const isEdit = Boolean(product);
  const [productName, setProductName] = useState(product?.productName || "");
  const [productCode, setProductCode] = useState(
    product?.productCode !== undefined ? String(product.productCode) : String(generateNextCode(products))
  );
  const [productSlug, setProductSlug] = useState(product?.productSlug || "");
  const [saving, setSaving] = useState(false);

  const handleNameChange = (val) => {
    setProductName(val);
    if (!isEdit) {
      setProductSlug(generateSlug(val));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanName = productName.trim();
    const cleanSlug = generateSlug(productSlug.trim() || cleanName);
    const numCode = parseInt(productCode, 10);

    if (!cleanName) {
      toast.error("Product name is required");
      return;
    }

    if (isNaN(numCode) || numCode <= 0) {
      toast.error("Product Code must be a valid positive integer (e.g. 101, 102)");
      return;
    }

    if (!cleanSlug) {
      toast.error("Product Slug is required");
      return;
    }

    // Client-side uniqueness check against existing products
    const duplicateCode = products.find(
      (p) => (!isEdit || p.id !== product.id) && Number(p.productCode) === numCode
    );
    if (duplicateCode) {
      toast.error(`Product Code '${numCode}' is already used by "${duplicateCode.productName}". Product code must be unique.`);
      return;
    }

    const duplicateName = products.find(
      (p) =>
        (!isEdit || p.id !== product.id) &&
        (p.productName || "").trim().toLowerCase() === cleanName.toLowerCase()
    );
    if (duplicateName) {
      toast.error(`Product Name "${cleanName}" already exists. Product name must be unique.`);
      return;
    }

    const duplicateSlug = products.find(
      (p) =>
        (!isEdit || p.id !== product.id) &&
        (p.productSlug || "").trim().toLowerCase() === cleanSlug.toLowerCase()
    );
    if (duplicateSlug) {
      toast.error(`Product Slug "${cleanSlug}" is already in use. Product slug must be unique.`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        productName: cleanName,
        productCode: numCode,
        productSlug: cleanSlug,
      };

      if (isEdit) {
        await api.patch(`/products/${product.id}`, payload);
        toast.success("Product updated successfully");
      } else {
        await api.post("/products", payload);
        toast.success("Product created successfully");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-[#111827] w-full max-w-lg p-6 relative shadow-2xl space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="product-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#6B7280] hover:text-[#111827] p-1.5 transition-colors"
        >
          <X size={20} weight="bold" />
        </button>

        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#6B7280]">
            {isEdit ? "Update Product" : "New Master Entry"}
          </div>
          <h2 className="font-heading font-black text-2xl tracking-tight text-[#111827]">
            {isEdit ? "Edit Product" : "Add Product"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-[#6B7280] mb-1">
              Product Name *
            </label>
            <input
              type="text"
              required
              value={productName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Smart Door Lock"
              className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>



          <div className="flex justify-end gap-2 pt-4 border-t border-[#E5E7EB]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-[#D1D5DB] text-sm font-bold hover:bg-[#F3F4F6] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002280] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving..." : isEdit ? "Update Product" : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
